import * as Scenes from "oc-threejs/Scenes";
import { v3 } from "oc-threejs/short3";
import { Jaco } from "./arrangement/Jaco";
import { Jaco3d } from "./arrangement3d/Jaco3d";

import { Object3D, Scene } from "three";

// Declare global STAGE
interface IStage {
  dirty: () => void;
  scene: Scene;
}
declare const STAGE: IStage;

export type Constructor<T = {}> = new (...args: any[]) => T;

export interface IJacoAppMinimum {
  arrangement: Jaco | null;
  arrangement3d: Object3D | null;
  update3d(): void;
  // Add other public methods if needed
}

export const JacoAppMinimumMixin = <TBase extends Constructor>(superClass: TBase) => {
  return class JacoAppMinMix extends superClass {
    arrangement: Jaco | null = null;
    /** @type {?Jaco3d} */
    arrangement3d: Object3D | null = null; // Can be Jaco3d or THREE.Object3D
    _currentUpdateId = 0;
    insideAnimationBlockUpdate3d = false;

    _dirty = false;

    ui: any; // Type for UI component

    constructor(...args: any[]) {
      super(...args);
    }

    dirty() {
      this._dirty = true;
      if (this.ui) {
        this.ui.dirty();
      }
      STAGE.dirty();
    }

    onBeforeRenderStage() {
      if (this._dirty) {
        this._dirty = false;
        this.update3d();
      }
    }

    update3d() {
      if (this.arrangement == null || this.insideAnimationBlockUpdate3d) {
        return;
      }
      this.update3dPlain();
      if ((this as any).trigger) {
        (this as any).trigger("afterUpdate3d");
      }
      STAGE.dirty();
      // const t1 = performance.now();
      // console.log('APP.update3d()',(t1-t0).toFixed(1),'ms');
    }

    update3dPlain() {
      // const t0 = performance.now();
      this._currentUpdateId++;
      if (this.arrangement) {
        this.arrangement.update3d();
        this._maybeAddArrangement3dToScene();
      }
      // Check if arrangement3d is valid before traversing
      if (this.arrangement3d) {
        this._traverseUpdate3d(this.arrangement3d);
        this._cleanUp();
      }
    }

    /**
     * Ermöglicht es, einen Listener für die Änderung von Werten des Arrangements zu registrieren.
     * Der Vergleich
     * @param getValue ein Callback, das einen Wert des Arrangements zurückgibt. Dieser Wert wird auf Änderung verglichen
     * @param onChange das Callback, das bei einer Veränderung aufgerufen wird
     * @return {onCallback}
     */
    onArrangementChange({
      getValue,
      onChange,
      valueEqualsComparator = (a: any, b: any) => a == b,
    }: {
      getValue: () => any;
      onChange: () => void;
      valueEqualsComparator?: (a: any, b: any) => boolean;
    }) {
      let oldValue: any = undefined;

      const onCallback = () => {
        const newValue = getValue();
        if (!valueEqualsComparator(oldValue, newValue)) {
          oldValue = newValue;
          onChange();
        }
      };

      if ((this as any).on) {
        (this as any).on("afterUpdate3d", onCallback);
      }

      // zum eventuellen Abräumen als Referenz zurückgeben
      return onCallback;
    }

    /**
     */
    _maybeAddArrangement3dToScene() {
      if (this.arrangement && this.arrangement3d !== this.arrangement.jaco3d) {
        if (this.arrangement3d) Scenes.remove(this.arrangement3d);
        this.arrangement3d = this.arrangement.jaco3d;
        if (this.arrangement3d) STAGE.scene.add(this.arrangement3d);
      }
    }

    /**
     */
    _cleanUp() {
      if (!this.arrangement3d) return;
      const toRemove = this._findObjectsToRemove(this.arrangement3d);
      for (let o of toRemove) {
        Scenes.remove(o);
      }
    }

    /**
     * @param {THREE.Object3D} object
     * @param {THREE.Object3D[]} [toRemove=[]]
     */
    _findObjectsToRemove(object: Object3D, toRemove: Object3D[] = []) {
      if ((object as any).forbidAutoRemove) {
        return toRemove;
      }
      if (
        (object as any)._updateId !== undefined &&
        (object as any)._updateId !== this._currentUpdateId
      ) {
        toRemove.push(object);
        return toRemove;
      }
      for (let o of object.children) {
        this._findObjectsToRemove(o, toRemove);
      }
      return toRemove;
    }

    /**
     * @param {THREE.Object3D} object
     */
    _traverseUpdate3d(object: Object3D) {
      if ((object as any).update3d !== undefined) {
        (object as any).update3d();
      }
      for (let o of object.children) {
        this._traverseUpdate3d(o);
      }
    }

    /**
     * Räumt alle 3D-Objekte ab und baut sie neu auf. Hilfreich, um manche Fehler zu ermitteln.
     * Sollte man allerdings nicht als Teil des normalen Programms verwenden, das wäre echt dirty, bäh!
     */
    clear3dForDebug() {
      Scenes.filter(STAGE.scene, (o) => (o as any).jaco).forEach((o) =>
        (o as any).jaco.clear3dForDebug(),
      );
    }

    /**
     * zu implementieren falls JacoCollection implementiert wird.
     * @example
     * @param typename
     * @param json
     */
    jacoFromJson(typename: string, json: any): Jaco {
      if ("Jaco" == typename) {
        return new Jaco(null).fromJSON(json);
      }
      throw new Error("typename not found: " + typename);
    }

    checkMMRNotUndefined(mmr: any) {
      if (mmr === null) {
        return;
      }
      if (mmr === undefined) {
        debugger;
        throw new Error("ModelMaterialRelation is undefined");
      }
      return mmr;
    }
  };
};
